/* eslint-disable @next/next/no-img-element */
import { CheckboxInput } from '@components/form/baseInputs'
import {
  AuthFill,
  Button,
  Comments,
  Date,
  DealerFinder,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  ModelGrid,
  Tel,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav, StepIndicator } from '@components/ui'
import { useCampaign, useDealer, useDealerContext } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import {
  closeOverlay,
  getModelImage,
  useFormSteps,
} from '@utils/helpers/index-client'
import { getContentFile, getModels, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { TestDriveSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from '@utils/helpers/stringHelpers'

import EVLogo from '../assets/icons/ev.svg'

type FormData = {
  modelImage?: string
  modelName?: string
  fuel?: string[]
  label?: string
  dealerID?: string
  dealerName?: string
  dealerDistance?: string
  address?: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
}

type SelectedDealer = {
  id?: number
  name?: string
  distance?: number
  address?: {
    address?: string
    address1?: string
    city?: string
    zip?: string
  }
}

const TestDrive = ({
  models,
  content,
  tracking,
  brand,
  dealer,
  model,
  query,
}) => {
  const { push, query: routerQuery } = useRouter()
  const clientDealer = useDealer()
  const dealerContext = useDealerContext()
  const campaign = useCampaign()
  const {
    formStep,
    user,
    expandedStep,
    goToStep,
    toggleExpand,
    closeAllExpands,
  } = useFormSteps(1)
  const [formData, setFormData] = useState<FormData>({})
  const [submitResponse, setSubmitResponse] = useState('')
  const [selectedDealer, setSelectedDealer] = useState<SelectedDealer>({})

  if (clientDealer) {
    // eslint-disable-next-line no-param-reassign
    dealer = clientDealer
  }

  // Alias for backward compatibility
  const step = goToStep

  const configID = (query?.configId || query?.code)?.replace(
    /[^a-zA-Z0-9]/g,
    '',
  )

  const initialValues = {
    first_name: '',
    last_name: '',
    email: '',
    tel: '',
    model: '',
    date: '',
    time: 'AM',
    notes: '',
    testdrive_type: 'STANDARD',
    consent: {
      user: false,
    },
    response_code: '',
    campaign_response_code: '',
    campaign_code: '',
    crm_response_source: '',
    is_dealer: query?.source === 'dealer' || query?.source === 'centre',
    dealer: 0,
    dealer_name: '',
    configID: configID ? `tme.one/${configID}` : '',
    is_lexus: brand === 'lexus',
    enhanced_testdrive: false,
    enhanced_vehicle: {
      id: '',
      label: '',
    },
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    utm_placement: '',
    utm_id: '',
  }

  const methods = useForm({
    resolver: yupResolver(TestDriveSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const handleSetDealer = (dealerObj) => {
    methods.setValue('dealer', dealerObj.id)
    methods.setValue('dealer_name', dealerObj.name)
    step(3, true)
    setSelectedDealer(dealerObj)
  }

  const isEnhancedTestDriveSelected = methods.watch('enhanced_testdrive')

  useEffect(() => {
    let params = routerQuery
    if (isEnhancedTestDriveSelected) {
      params = {
        ...routerQuery,
        ev_enhance: methods.getValues('enhanced_vehicle.id'),
      }
    } else {
      delete params.ev_enhance
    }

    push({ query: { ...params } }, undefined, {
      shallow: true,
    })
  }, [isEnhancedTestDriveSelected])

  useEffect(() => {
    if (query?.type?.toLowerCase() === '48hr') {
      methods.setValue('testdrive_type', '48HR')
    }
    if (query?.type?.toLowerCase() === '24hr') {
      methods.setValue('testdrive_type', '24HR')
    }
    if (query?.featured?.toLowerCase() === 'motability') {
      methods.setValue('testdrive_type', 'MOTABILITY TD')
    }
    if (query?.featured?.toLowerCase() === 'fleet') {
      methods.setValue('testdrive_type', 'FLEET TD')
    }
    if (query?.utm_source) {
      methods.setValue('utm_source', query?.utm_source)
    }
    if (query?.utm_medium) {
      methods.setValue('utm_medium', query?.utm_medium)
    }
    if (query?.utm_campaign) {
      methods.setValue('utm_campaign', query?.utm_campaign)
    }
    if (query?.utm_content) {
      methods.setValue('utm_content', query?.utm_content)
    }
    if (query?.utm_term) {
      methods.setValue('utm_term', query?.utm_term)
    }
    if (query?.utm_placement) {
      methods.setValue('utm_placement', query?.utm_placement)
    }
    if (query?.utm_id) {
      methods.setValue('utm_id', query?.utm_id)
    }
  }, [query])

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.testDrive) {
      methods.setValue('campaign_response_code', campaign.testDrive)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (model) {
      if (brand === 'lexus') {
        methods.setValue('response_code', model?.responsecodes?.test_drive)
      }
      methods.setValue('model', model.d4t4.id)
      setFormData((state) => {
        return {
          ...state,
          modelImage: getModelImage(
            brand,
            model?.image?.id,
            model?.image?.grade,
            model?.image?.color,
            'webp',
            model?.image?.fallback,
          ),
          modelName: model.name,
          fuel: model?.filters?.fuel,
        }
      })
      step(dealer ? 3 : 2, false)
    }
  }, [model])

  useEffect(() => {
    if (dealer) {
      methods.setValue('is_dealer', true)
      methods.setValue('testdrive_type', 'TWENTYFOURHR')
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      // Use preferredDealerName from postMessage if available, otherwise use API result
      const dealerName = dealerContext?.preferredDealerName || dealer.name
      methods.setValue('dealer_name', dealerName)
      setSelectedDealer(dealer)
      step(model ? 3 : 1, false)
    }
  }, [dealer, dealerContext?.preferredDealerName])

  // Step change handlers
  const handleVehicleChange = () => {
    step(1, true)
    closeAllExpands()
    methods.setValue('dealer', 0)
    methods.setValue('dealer_name', '')
    setSelectedDealer({})
    setFormData((state) => ({
      ...state,
      modelImage: '',
      modelName: '',
      fuel: undefined,
      dealerID: '',
      dealerName: '',
      dealerDistance: '',
      address: '',
    }))
    setSubmitResponse('')
  }

  const handleDealerChange = () => {
    step(2, true)
    closeAllExpands()
    methods.setValue('dealer', 0)
    methods.setValue('dealer_name', '')
    setSelectedDealer({})
  }

  const handleDetailsChange = () => {
    step(4, true)
    closeAllExpands()
    setFormData((state) => ({
      ...state,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    }))
  }

  // Step indicator components using StepIndicator
  const VehicleStep = () => (
    <StepIndicator
      stepNumber={1}
      label={brand === 'lexus' ? 'Car' : 'Vehicle'}
      currentStep={formStep}
      isCompleted={!!formData.modelName}
      isExpanded={expandedStep === 'vehicle'}
      summaryText={
        formData.modelName
          ? `${formData.modelName}${formData.label ? ` ${formData.label}` : ''}`
          : undefined
      }
      onChangeClick={handleVehicleChange}
      onExpandToggle={() => toggleExpand('vehicle')}
      trackingName="test-drive"
      expandedContent={
        formData.modelName ? (
          <>
            <div className="text-lg font-medium">{formData.modelName}</div>
            {formData.fuel !== undefined &&
              formData.fuel.some(
                (f) => f.toLowerCase() !== 'petrol' && f.toLowerCase() !== 'diesel',
              ) && (
                <div
                  className={`${
                    brand === 'toyota' ? 'font-medium text-blue' : ''
                  } uppercase`}
                >
                  {formData.fuel.join(' & ')}
                </div>
              )}
            <img
              className="w-full font-medium mt-4"
              src={formData.modelImage}
              alt={formData.modelName}
            />
          </>
        ) : undefined
      }
    />
  )

  const DealerStep = () => (
    <StepIndicator
      stepNumber={2}
      label={brand === 'lexus' ? 'Centre' : 'Dealer'}
      currentStep={formStep}
      isCompleted={!!selectedDealer.name}
      isExpanded={expandedStep === 'dealer'}
      summaryText={selectedDealer?.name}
      onChangeClick={handleDealerChange}
      onExpandToggle={() => toggleExpand('dealer')}
      trackingName="test-drive"
      expandedContent={
        selectedDealer?.name ? (
          <>
            <div className="text-tiny lowercase">
              {selectedDealer.distance &&
                `${(selectedDealer.distance * 0.62137119).toFixed(2)} miles`}
            </div>
            <div className="my-[10px] font-medium">{selectedDealer?.name}</div>
            {selectedDealer?.address?.address && (
              <div className="normal-case">
                {selectedDealer?.address?.address}
              </div>
            )}
            {selectedDealer?.address?.address1 && (
              <div className="normal-case">
                {selectedDealer?.address?.address1}
              </div>
            )}
            {selectedDealer?.address?.city && (
              <div className="normal-case">{selectedDealer?.address?.city}</div>
            )}
            {selectedDealer?.address?.zip && (
              <div className="normal-case">{selectedDealer?.address?.zip}</div>
            )}
          </>
        ) : undefined
      }
    />
  )

  const DetailsStep = () => (
    <StepIndicator
      stepNumber={3}
      label="Details"
      currentStep={formStep}
      isCompleted={!!formData.first_name}
      isExpanded={expandedStep === 'details'}
      summaryText={formData.first_name}
      onChangeClick={handleDetailsChange}
      onExpandToggle={() => toggleExpand('details')}
      trackingName="test-drive"
      expandedContent={
        formData.first_name ? (
          <>
            <div>{`${formData.first_name} ${formData.last_name}`}</div>
            <div>{formData.email}</div>
            <div>{formData.phone}</div>
          </>
        ) : undefined
      }
    />
  )

  const onSubmit = async (data) => {
    setSubmitResponse('')
    const formatData = data

    if (data.enhanced_testdrive) {
      formatData.notes = `Customer would like to enhance their visit and test drive the ${data.enhanced_vehicle.label}`
    }

    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/test-drive`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formatData,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          formatData.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
        steps={[VehicleStep(), DealerStep(), DetailsStep()]}
      />
      <FormWrapper>
        <FormStep
          title={content.car.title}
          subtitle="<p> If you're a fleet manager or small business owner, <a href='/fleet/callback'  rel='noopener noreferrer' data-gt-name='link' data-gt-action=`callback-click-link` data-gt-label='callback-link' target='_blank'>click here</a> to request a demo.</p>"
          step={formStep}
          active={formStep === 1}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'select-model',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <ModelGrid
            models={models}
            showFilter={content.car.filter}
            setModel={(selectedModel) => {
              if (brand === 'lexus') {
                methods.setValue(
                  'response_code',
                  selectedModel?.responsecodes?.test_drive,
                )
              }

              methods.resetField('enhanced_testdrive')

              if (
                brand === 'toyota' &&
                selectedModel.d4t4.id === 'All New RAV4 Hybrid'
              ) {
                methods.setValue('enhanced_vehicle', {
                  id: 'rav4PluginHybrid',
                  label: 'RAV4 Plug-in Hybrid',
                })
              }

              if (brand === 'toyota' && selectedModel.d4t4.id === 'RAV4 PHEV') {
                methods.setValue('enhanced_vehicle', {
                  id: 'bx4x',
                  label: 'All-Electric bZ4X',
                })
              }

              if (brand === 'lexus' && selectedModel.d4t4.id.includes('NX')) {
                methods.setValue('enhanced_vehicle', {
                  id: 'rz',
                  label: 'All-Electric RZ',
                })
              }

              if (brand === 'lexus' && selectedModel.d4t4.id.includes('RX')) {
                methods.setValue('enhanced_vehicle', {
                  id: 'rz',
                  label: 'All-Electric RZ',
                })
              }

              methods.setValue('model', selectedModel.d4t4.id)
              setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }, 100)
              if (methods.getValues('dealer')) {
                step(3, true)
              } else {
                step(2, true)
              }

              setFormData((state) => {
                return {
                  ...state,
                  modelImage: getModelImage(
                    brand,
                    selectedModel?.image?.id,
                    selectedModel?.image?.grade,
                    selectedModel?.image?.color,
                    'webp',
                    selectedModel?.image?.fallback,
                  ),
                  modelName: selectedModel.name,
                  fuel: selectedModel?.filters?.fuel || undefined,
                }
              })
            }}
          />
        </FormStep>
      </FormWrapper>

      <FormStep
        step={formStep}
        active={formStep === 2}
        user={user}
        noContainer={true}
        tracking={{
          conversion: 0,
          name: tracking.form,
          step: 'select-dealer-get-location',
          variant: tracking.variant,
          page: tracking.page,
        }}
      >
        <div className="relative z-[1]">
          <DealerFinder setDealer={handleSetDealer} />
        </div>
      </FormStep>

      <FormWrapper>
        <FormStep
          title={content.details.title}
          step={formStep}
          active={formStep === 3}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'contact-details',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <FirstName />
              <LastName />
              <Email />
              <Tel
                optional
                helper="Leave your phone number if you would like to discuss your test drive over the phone."
              />
              {brand === 'lexus' && (
                <Date
                  showAmPm={true}
                  helper="Let us know if you have a preferred date or time and we'll do our best to arrange your booking for this time."
                />
              )}
              <Comments
                label="Additional Comments"
                helper="Let us know if you have any specific requirements."
                characterLimit={255}
              />

              {methods.getValues('enhanced_vehicle.id') && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 md:gap-4 items-start justify-top mt-8 mb-4 md:mb-2">
                  <div className="pt-5 pb-6 px-6 border-2 border-blue bg-primary md:col-span-2 lg:col-span-1">
                    <div className="flex flex-col-reverse md:flex-row items-end md:items-center gap-4 mb-4">
                      <h4 className="font-semibold">
                        Would you like to enhance your visit and test drive the{' '}
                        {methods.getValues('enhanced_vehicle.label')}?
                      </h4>
                      <EVLogo className={'text-blue shrink-0'} />
                    </div>
                    <CheckboxInput
                      name={'enhanced_testdrive'}
                      label={`Yes, I would like to test drive the ${methods.getValues(
                        'enhanced_vehicle.label',
                      )}`}
                    />
                  </div>
                </div>
              )}
              <MarketingConsent />

              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'test-drive'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      settings: settings.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: 'test-drive',
        initialStep: 'select-model',
      },
      description: `A test drive is the best way to check which ${upperCaseFirst(
        brand,
      )} is the one for you and booking is easy. Interested? Get in touch directly with one of our ${upperCaseFirst(
        brand,
      )} experts.`,
      ...(await getPageData(context, form)),
    },
  }
}

export default TestDrive
